import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { staggerContainer, staggerItem } from "@/hooks/useAnimations";
import { services } from "@/data/services";

export default function Footer() {
  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Insights", path: "/blogs" },
    { name: "Resources", path: "/resources" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <footer className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #0A0F1C 0%, #070D1A 50%, #060B18 100%)" }} data-testid="site-footer">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")" }} />
      <div className="section-separator" />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-10">
        {/* Editorial large CTA */}
        <motion.div className="mb-20" initial={{ opacity: 0, y: 40, filter: "blur(10px)" }} whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[#D97706]/60 text-[11px] tracking-[0.35em] uppercase font-body font-semibold mb-5">Ready to grow?</p>
          <h2
            className="font-display font-bold text-[#FDFBF7] tracking-tighter leading-[0.95]"
            style={{ fontSize: "clamp(3.5rem, 10vw, 9rem)" }}
          >
            Let's Talk.
          </h2>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              className="group relative inline-flex items-center gap-3 pl-8 pr-2.5 py-2.5 rounded-full overflow-hidden"
              data-testid="footer-cta-button"
            >
              <div className="absolute inset-0 rounded-full" style={{ background: "linear-gradient(135deg, #D97706, #FBBF24)" }} />
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div className="absolute -inset-full top-0 block h-full w-1/2 -skew-x-12 bg-gradient-to-r from-transparent to-white/25 opacity-0 group-hover:opacity-100 group-hover:translate-x-[350%] transition-all duration-700" />
              </div>
              <span className="relative z-10 text-sm font-heading font-bold text-[#0A0F1C]">Book Free Consultation</span>
              <span className="relative z-10 w-11 h-11 rounded-full bg-[#0A0F1C]/20 flex items-center justify-center group-hover:bg-[#0A0F1C]/30 transition-colors">
                <ArrowRight size={16} className="text-[#0A0F1C]" />
              </span>
            </Link>
            <a href="https://wa.me/919500121211" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 border border-white/10 text-white/50 font-heading font-semibold text-sm rounded-full hover:border-[#D97706]/40 hover:text-[#D97706] transition-all duration-300">
              Chat on WhatsApp
            </a>
          </div>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 pt-12 border-t border-white/10" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          {/* Company info */}
          <motion.div className="lg:col-span-1" variants={staggerItem}>
            <Link to="/" className="inline-block mb-6 group">
              <div className="relative inline-flex items-center">
                <div className="absolute inset-0 -m-2 rounded-xl bg-[#D97706]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <img
                  src="/logo_transparent.png"
                  alt="AS Consultech India Private Limited"
                  className="h-12 md:h-14 w-auto object-contain relative z-10"
                />
              </div>
            </Link>
            <p className="text-white/50 text-sm leading-relaxed font-body mb-4">
              Strategic compliance & growth partner for startups and MSMEs across Tamil Nadu and India. Building complete business systems — not just filing returns.
            </p>
            <p className="text-xs text-white/25 font-body">CIN: U70200TZ2026PTC037659</p>
            <p className="text-xs text-white/20 font-body mt-1">GST Consultant in Dindigul, Tamil Nadu</p>
            {/* Entity stacking: exact-match navigational anchors for branded query signals */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3">
              <a href="https://asconsultech.com" rel="canonical" className="text-[10px] text-white/20 font-body hover:text-[#D97706] transition-colors" data-testid="footer-brand-anchor">Visit AS Consultech</a>
              <span className="text-white/10 text-[10px]">·</span>
              <a href="https://www.linkedin.com/company/asconsultech" target="_blank" rel="noopener noreferrer" className="text-[10px] text-white/20 font-body hover:text-[#D97706] transition-colors" data-testid="footer-linkedin">LinkedIn</a>
              <span className="text-white/10 text-[10px]">·</span>
              <a href="https://share.google/IK1RyoaojlEMAupbf" target="_blank" rel="noopener noreferrer" className="text-[10px] text-white/20 font-body hover:text-[#D97706] transition-colors" data-testid="footer-google-reviews">Google Reviews</a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={staggerItem}>
            <h4 className="font-heading font-bold text-[#FDFBF7] text-sm uppercase tracking-wider mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-white/50 text-sm font-body hover:text-[#D97706] transition-colors inline-flex items-center gap-1 group" data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}>
                    {link.name}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div variants={staggerItem}>
            <h4 className="font-heading font-bold text-[#FDFBF7] text-sm uppercase tracking-wider mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s.id}>
                  <Link to={`/services/${s.slug}`} className="text-white/50 text-sm font-body hover:text-[#D97706] transition-colors" data-testid={`footer-service-${s.slug}`}>
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div variants={staggerItem}>
            <h4 className="font-heading font-bold text-[#FDFBF7] text-sm uppercase tracking-wider mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:+919500121211" className="flex items-start gap-3 text-white/50 hover:text-[#D97706] transition-colors" data-testid="footer-phone">
                  <Phone size={16} className="mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-body">+91 95 00 12 12 11</span>
                </a>
              </li>
              <li>
                <a href="mailto:contact@asconsultech.com" className="flex items-start gap-3 text-white/50 hover:text-[#D97706] transition-colors" data-testid="footer-email">
                  <Mail size={16} className="mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-body">contact@asconsultech.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/50">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-body leading-relaxed">No. 2/582, Anna Nagar, Gandhiji Colony, Kalikkampatti, Dindigul, Tamil Nadu, 624303</span>
                </div>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/25 font-body">&copy; {new Date().getFullYear()} AS Consultech India Private Limited · CIN: U70200TZ2026PTC037659 · Dindigul, Tamil Nadu</p>
          <p className="text-[10px] text-white/15 font-body mt-1">Serving businesses in Dindigul · Madurai · Trichy · Coimbatore · Salem · Erode · Pan Tamil Nadu · Pan India</p>
          <div className="flex items-center gap-6">
            <a href="https://asconsultech.com" className="text-[10px] text-white/15 font-body hover:text-[#D97706] transition-colors" rel="canonical">AS Consultech official website</a>
            <Link to="/privacy-policy" className="text-xs text-white/25 hover:text-[#D97706] transition-colors font-body">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-xs text-white/25 hover:text-[#D97706] transition-colors font-body">Terms of Service</Link>
            <span className="text-xs text-white/15 font-body">Made in Tamil Nadu 🇮🇳</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
